import { Dropdown, Grid, Tag, Typography } from "antd";
import { FontAwesomeIcon, Icon, SvgIcon } from "assets/icons";
import CustomListFileImage from "components/CustomListFileImage";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import { STATUS_ORDER, STOCK_STATUS } from "config/constants";
import { ORDER_TYPE, SHIPPING_PROVIDER_TYPE } from "features/Cart/constants";
import { PAYMENT_STATUS } from "features/Order/constants";
import DeliveryDeadline from "features/Order/Detail/components/DeliveryDeadline";
import { useGetWarehouses, useOrderPermissions } from "hooks/order";
import { useSalesChannelInputOptions } from "hooks/salesChannel";
import { t } from "i18next";
import moment from "moment";
import { SELLER_APPROVAL_STATUS } from "pages/App/Agent/constants";
import {
  renderColorTagByPaymentStatus,
  renderOrderPriority,
  renderPaymentMethod,
  renderRemainingDeliverTime,
  renderShippingMethod,
  renderStatus,
  renderStockStatus,
  renderStringOrderPromotionType,
  renderStringTagByPaymentStatus,
  renderVATAndFullStockRequired,
  tagColorSalesChannel,
} from "pages/App/Order/conts";
import { useOrder } from "pages/App/Order/hooks";
import { Link } from "react-router-dom";
import { buildQueryString, formatCurrency, getQuery } from "utils/helperFuncs";
import FilterCancelReason from "../FilterCancelReason";
import FilterOrder from "../FilterOrder";
import SuccessDeliveryButton from "../SuccessDeliveryButton";

export const inStockOption = [
  { value: STOCK_STATUS.FULL, label: t("common.full") },
  { value: STOCK_STATUS.LACK, label: t("common.lack") },
  { value: STOCK_STATUS.OUT_OF_STOCK, label: t("common.outOfStock") },
];

export const promotionTypeOption = [
  { value: "NO_PROMOTION", label: "Không có ưu đãi" },
  { value: "GROUP_BUYING_CAMPAIGN", label: "Khuyến mãi mua chung" },
  { value: "PRE_ORDER_CAMPAIGN", label: "Khuyến mãi đặt trước" },
  { value: "PRE_ORDER_POLICY", label: "Đặt trước" },
];

export const paymentStatusOption = [
  { value: PAYMENT_STATUS.PENDING, label: "Chưa thanh toán" },
  { value: PAYMENT_STATUS.WAITING_APPROVAL, label: "Chờ duyệt thanh toán" },
  { value: PAYMENT_STATUS.PAID, label: "Đã thanh toán" },
];

const useColumnsOrder = (params, setParams, filterTags, setFilterTags) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { data: warehousesOptions } = useGetWarehouses();

  const {
    orderPriorityOptions,
    shippingOptions,
    paymentMethodOptions,
    statusOptions,
    fullStockRequiredOption,
    vatOption,
  } = useOrder();

  const { canCompleteOrder } = useOrderPermissions();

  const { convertedData: dataSalesChannel } = useSalesChannelInputOptions();

  const GET_QUERY = getQuery();

  const onFilter = (value) => {
    const newParams = {
      ...params,
      filters: { ...params.filters, ...value },
      pagination: {
        offset: 0,
        limit: 10,
      },
    };
    setParams(newParams);
    buildQueryString({
      params: {
        ...newParams.filters,
        ...newParams.pagination,
        activeKey: GET_QUERY?.activeKey,
      },
    });
  };

  const renderDropdownFilter = (
    options,
    keyObj,
    typeSelection = "checkbox",
    params,
    setParams,
    paginationData = undefined
  ) => {
    return (
      <Dropdown
        overlayClassName="dropdown-overlay"
        trigger="click"
        getPopupContainer={() => document.body}
        overlay={
          <FilterOrder
            options={options}
            keyObj={keyObj}
            onFilter={onFilter}
            pagination={paginationData}
            param={params}
            setParam={setParams}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
            typeSelection={typeSelection}
          />
        }
        placement="bottomRight"
      >
        <FontAwesomeIcon icon={Icon.faFilter} />
      </Dropdown>
    );
  };

  const handleSort = (by) => {
    //check sort have orderType is ASC or DESC
    if (params.sort[0]?.orderType === "ASC") {
      const newParams = {
        ...params,
        sort: [{ by: by, orderType: "DESC" }],
      };
      setParams(newParams);
    } else {
      const newParams = {
        ...params,
        sort: [{ by: by, orderType: "ASC" }],
      };
      setParams(newParams);
    }
  };

  const all = {
    //STT (fixed column)
    index: {
      fixed: screens.lg ? "left" : undefined,
      title: <Typography.Text>{t("order.orderList.index")}</Typography.Text>,
      render: (text, record, index) => index + 1,
      width: 50,
      align: "center",
    },
    //Ngày tạo (fixed column)
    createdAt: {
      fixed: screens.lg ? "left" : undefined,
      title: <div className="title-table">{t("order.orderList.createdAt")}</div>,
      dataIndex: "createdAt",
      width: 110,
      sorter: true,
    },
    //Loại đơn (fixed column)
    orderType: {
      fixed: screens.lg ? "left" : undefined,
      title: (
        <div className="title-table">
          Loại đơn
          {renderDropdownFilter(promotionTypeOption, "promotionTypes", "checkbox")}
        </div>
      ),

      render: (text, record, index) => {
        return renderStringOrderPromotionType(record?.promotionType);
      },
      width: 180,
    },
    //Mã đơn (fixed column)
    orderCode: {
      fixed: screens.lg ? "left" : undefined,
      title: <Typography.Text>{t("order.orderList.orderCode")}</Typography.Text>,
      dataIndex: "number",
      render: (text, record) => {
        return (
          <Link to={`/order/${record?.id}`}>
            <b>{text}</b>
          </Link>
        );
      },
      width: 180,
    },
    //GIÁ TRỊ ĐƠN
    subTotal: {
      title: (
        <div className="title-table">
          {t("order.orderList.totalOrder")}
          <SvgIcon.SortIcon onClick={() => handleSort("GRAND_TOTAL")} />
        </div>
      ),
      align: "end",
      width: 130,
      render: (text, record) => {
        const totalOrder = record?.orderType !== ORDER_TYPE.CUSTOMER ? record?.total : record.customerTotal;
        return formatCurrency(totalOrder);
      },
    },
    //KHO XUẤT
    exportedWarehouse: {
      title: (
        <div className="title-table">
          {t("order.orderList.exportWarehouse")}
          {renderDropdownFilter(warehousesOptions, "exportedWarehouseIDs", "checkbox")}
        </div>
      ),
      dataIndex: "exportedWarehouse",
      width: 110,
    },
    //KHÁCH HÀNG
    receiverName: {
      title: (
        <div className="title-table">
          {t("order.orderList.receiverName")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
        </div>
      ),
      // dataIndex: "receiverName",
      width: 400,
      render: (_, record) => {
        return (
          <div>
            <b>{record.seller.code}</b>
            <CustomTextHiddenLine text={record.seller.fullName} line={2} />
          </div>
        );
      },
    },
    //ĐỘ ƯU TIÊN
    orderPriority: {
      title: (
        <div className="title-table">
          {t("order.orderList.orderPriority")}
          {renderDropdownFilter(orderPriorityOptions, "priorities", "checkbox")}
        </div>
      ),
      dataIndex: "orderPriority",
      render: (text) => {
        return renderOrderPriority(text);
      },
      width: 130,
    },
    //Phương thức giao hàng
    shippingType: {
      title: (
        <div className="title-table">
          Phương thức giao hàng
          {renderDropdownFilter(shippingOptions, "shippingTypes", "checkbox")}
        </div>
      ),
      dataIndex: "shippingType",
      // Tên PTVC lấy thẳng từ BE (`shippingTypeName`) — đổi tên trong DB là UI đổi theo, khớp
      // cách Chi tiết đơn hiển thị. `renderShippingMethod` chỉ còn là fallback khi BE chưa trả.
      render: (text, record) => {
        return record?.shippingTypeName || renderShippingMethod(text);
      },
      width: 200,
    },
    //PHƯƠNG THỨC THANH TOÁN
    paymentMethod: {
      title: (
        <div className="title-table">
          {t("order.orderList.paymentMethod")}
          {renderDropdownFilter(paymentMethodOptions, "paymentMethods", "checkbox")}
        </div>
      ),
      dataIndex: "paymentMethod",
      render: (text) => {
        return renderPaymentMethod(text);
      },
      width: 150,
    },
    //Trạng thái thanh toán
    paymentStatus: {
      title: (
        <div className="title-table">
          {t("order.orderList.statusPayment")}
          {renderDropdownFilter(paymentStatusOption, "paymentStatuses", "checkbox")}
        </div>
      ),
      dataIndex: "paymentMethod",
      render: (_, record) => {
        return record?.paymentStatus ? (
          <Tag color={renderColorTagByPaymentStatus(record?.paymentStatus)}>
            {renderStringTagByPaymentStatus(record?.paymentStatus)}
          </Tag>
        ) : (
          "- -"
        );
      },
      width: 200,
    },
    //Trạng thái đơn hàng
    status: {
      title: (
        <div className="title-table">
          Trạng thái đơn hàng
          {renderDropdownFilter(statusOptions, "statuses", "checkbox")}
        </div>
      ),
      dataIndex: "status",
      render: (text) => {
        return renderStatus(text);
      },
      width: 150,
    },
    salesPerson: {
      title: <div className="title-table">{t("seller.salesMan")}</div>,
      width: 150,
      render: (_, record) => {
        return record?.salesman?.fullname ? (
          <CustomTextHiddenLine line={2} text={record?.salesman?.fullname} />
        ) : (
          <Typography.Text style={{ color: "#9CADC4" }}>--</Typography.Text>
        );
      },
    },
    salesChannel: {
      fixed: screens.lg ? "right" : undefined,
      title: (
        <div className="title-table">
          {t("order.orderDetail.saleChannel")}
          {renderDropdownFilter(dataSalesChannel, "saleChannelIDs", "checkbox")}
        </div>
      ),
      dataIndex: "saleChannel",
      render: (_, record) => {
        return <Tag color={tagColorSalesChannel?.[record?.saleChannel?.type]}>{record?.saleChannel?.name}</Tag>;
      },
      width: 150,
    },
    //Người tạo (fixed column)
    createdByStatus: {
      fixed: screens.lg ? "right" : undefined,
      title: <div className="title-table">{t("order.orderList.createdBy")}</div>,
      dataIndex: "createdBy",
      width: 200,
      align: "center",
      render: (_, record) => {
        return (
          <>
            <div>{record?.createdBy}</div>
            {record?.seller?.approvalStatus !== SELLER_APPROVAL_STATUS.APPROVED && (
              <div style={{ fontSize: "12px", color: "#5B6673" }}>(Chờ xác thực)</div>
            )}
          </>
        );
      },
    },
    //TÌNH TRẠNG TỒN
    inStockStatus: {
      title: (
        <div className="title-table">
          {t("order.orderList.inStockStatus")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}

          {renderDropdownFilter(inStockOption, "stockStatus", "radio")}
        </div>
      ),
      dataIndex: "inStockStatus",
      width: 160,
      render: (text) => renderStockStatus(text),
    },
    //GIAO KHI CHƯA ĐỦ HÀNG
    fullStockRequired: {
      title: (
        <div className="title-table">
          {t("order.orderList.fullStockRequired")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
          {renderDropdownFilter(fullStockRequiredOption, "fullStockRequired", "checkbox")}
        </div>
      ),
      dataIndex: "fullStockRequired",
      width: 150,
      align: "center",
      render: (text) => renderVATAndFullStockRequired(text),
    },
    //XUẤT VAT
    vat: {
      title: (
        <div className="title-table">
          {t("order.orderList.exportVAT")}
          {/* <FontAwesomeIcon icon={Icon.faFilter} /> */}
          {renderDropdownFilter(vatOption, "vat", "checkbox")}
        </div>
      ),
      width: 120,
      dataIndex: "vat",
      align: "center",
      render: (text) => renderVATAndFullStockRequired(text),
    },
    //THỜI GIAN CÒN LẠI
    remainingDeliverTime: {
      title: (
        <div className="title-table">
          {t("order.orderList.remainingDeliverTime")}
          <SvgIcon.SortIcon onClick={() => handleSort("CREATED_AT")} />
        </div>
      ),
      dataIndex: "remainingDeliverTime",
      width: 180,
      align: "center",
      render: (text) => {
        return renderRemainingDeliverTime(text);
      },
    },
    //ĐỊA CHỈ NGƯỜI NHẬN
    receiverContactAddress: {
      title: <Typography.Text>{t("order.orderList.receiverContactAddress")}</Typography.Text>,
      dataIndex: "receiverContactAddress",
      width: 250,
    },
    //Thời gian giao hàng
    deliveryDeadline: {
      title: <Typography.Text>Thời gian giao hàng</Typography.Text>,
      render: (_, record) => {
        return (
          <DeliveryDeadline
            deliveryDeadline={record?.deliveryDeadline}
            expectedDeliveryFrom={record.minDeliveryDate}
            expectedDeliveryTo={record.maxDeliveryDate}
            timeCheck={
              status === "COMPLETED" || status === "CANCELED"
                ? moment(record?.fullEvents[record?.fullEvents.length - 1].createdAt)
                : moment()
            }
            statusCheck={status === "COMPLETED" || status === "CANCELED"}
          />
        );
      },
      width: 200,
      align: "left",
      dataIndex: "deliveryDeadline",
      sorter: true,
    },
    //ĐƠN VỊ VẬN CHUYỂN
    shippingUnit: {
      title: <Typography.Text>{t("order.orderList.shippingUnit")}</Typography.Text>,
      dataIndex: "shippingUnit",
      width: 230,
    },
    //NGÀY GIAO HÀNG THÀNH CÔNG
    successfulDeliveryDate: {
      title: <Typography.Text>{t("order.orderList.successfulDeliveryDate")}</Typography.Text>,
      dataIndex: "successfulDeliveryDate",
      width: 150,
    },
    //SỐ ĐIỆN THOẠI ĐVVC
    shipperTelephone: {
      title: <Typography.Text>{t("order.orderList.shipperTelephone")}</Typography.Text>,
      width: 150,
      dataIndex: "shipperTelephone",
    },
    //ĐỊA ĐIỂM GIAO HÀNG
    senderAddress: {
      title: <Typography.Text>{t("order.orderList.senderAddress")}</Typography.Text>,
      render: (record) => <Typography.Text>{record?.receiverContactAddress}</Typography.Text>,
      width: 300,
    },
    //GHI CHÚ ĐANG GIAO
    noteShipping: {
      title: <Typography.Text>{t("order.orderList.noteShipping")}</Typography.Text>,
      dataIndex: "noteShipping",
      width: 450,
    },
    //SỐ ĐIỆN THOẠI KHÁCH HÀNG
    receiverContactPhone: {
      title: <Typography.Text>{t("order.orderList.receiverContactPhone")}</Typography.Text>,
      dataIndex: "receiverContactPhone",
      width: 150,
    },
    //Giao thành công
    linkShippingSuccess: {
      render: (text, record) => canCompleteOrder && <SuccessDeliveryButton orderID={record.id} />,
      width: 150,
    },
    //GHI CHÚ ĐƠN HÀNG
    note: {
      title: <Typography.Text>{t("order.orderList.note")}</Typography.Text>,
      dataIndex: "note",
      width: 150,
    },
    //LÝ DO HỦY ĐƠN
    cancelReason: {
      title: (
        <Typography.Text>
          {t("order.orderList.cancelReason")}{" "}
          <FilterCancelReason
            keyObj="cancelReasons"
            onFilter={onFilter}
            params={params}
            setParam={setParams}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
          />
        </Typography.Text>
      ),
      width: 250,
      render: (_, record) => {
        return (
          <Typography.Text>
            {t("order.cancelReasons.cancelBy")}{" "}
            {record?.fullEvents?.[record?.fullEvents?.length - 1]?.createdBy?.type === "SELLER"
              ? t("order.cancelReasons.seller")
              : t("order.cancelReasons.staff")}{" "}
            {t("order.cancelReasons.by")}:
            <br />
            {record?.cancelReason}
          </Typography.Text>
        );
      },
    },
    //Thông tin đơn vị vận chuyển
    transportUnitInformation: {
      title: <Typography.Text>Thông tin đơn vị vận chuyển</Typography.Text>,
      width: 200,
      render: (_, record) => {
        const busConfig = record?.shippingConfiguration?.busConfig;
        const deliver3PartyConfig = record?.shippingConfiguration?.deliver3PartyConfig;

        switch (record.shippingType) {
          case "PICK_UP_COUNTER":
            return "<Khách đến lấy hàng>";
          case "URBAN_COD":
            return (
              <div>
                {record.shipperName || "--"} - {record.shipperTelephone || "--"}
              </div>
            );
          case "BUS":
            return (
              <div>
                <div>
                  {busConfig.busName} - {busConfig.telephone}
                </div>
                <div>{busConfig.busStation}</div>
              </div>
            );
          case "DELIVER_3PARTY":
          case "DELIVER_3PARTY_INSTANT":
            return SHIPPING_PROVIDER_TYPE[deliver3PartyConfig?.carrierMethod];
          default:
            return "<Hủy đơn trước khi giao giao vận>";
        }
      },
    },
    //Ảnh giao hàng
    image: {
      title: <Typography.Text>{t("order.orderList.image")}</Typography.Text>,
      dataIndex: "fileURLs",
      width: 150,
      align: "center",
      render: (_, record, index) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CustomListFileImage listImage={record?.fileURLs} size={80} />
        </div>
      ),
    },
  };

  const {
    //STT (fixed column)
    index,
    //Ngày tạo (fixed column)
    createdAt,
    //Loại đơn (fixed column)
    orderType,
    //Mã đơn (fixed column)
    orderCode,
    //GIÁ TRỊ ĐƠN
    subTotal,
    //KHO XUẤT
    exportedWarehouse,
    //KHÁCH HÀNG
    receiverName,
    //ĐỘ ƯU TIÊN
    orderPriority,
    //Phương thức giao hàng
    shippingType,
    //PHƯƠNG THỨC THANH TOÁN
    paymentMethod,
    //Trạng thái thanh toán
    paymentStatus,
    //Trạng thái đơn hàng
    status,
    //Kênh bán (fixed column)
    salesChannel,
    //Người tạo (fixed column)
    createdByStatus,
    //TÌNH TRẠNG TỒN
    inStockStatus,
    //GIAO KHI CHƯA ĐỦ HÀNG
    fullStockRequired,
    //XUẤT VAT
    vat,
    //THỜI GIAN CÒN LẠI
    remainingDeliverTime,
    //ĐỊA CHỈ NGƯỜI NHẬN
    receiverContactAddress,
    //Thời gian giao hàng
    deliveryDeadline,
    //ĐƠN VỊ VẬN CHUYỂN
    shippingUnit,
    //GHI CHÚ NỘI BỘ
    //NGÀY GIAO HÀNG THÀNH CÔNG
    successfulDeliveryDate,
    //SỐ ĐIỆN THOẠI ĐVVC
    shipperTelephone,
    //ĐỊA ĐIỂM GIAO HÀNG
    senderAddress,
    //GHI CHÚ ĐANG GIAO
    noteShipping,
    //SỐ ĐIỆN THOẠI KHÁCH HÀNG
    receiverContactPhone,
    //Giao thành công
    linkShippingSuccess,
    //GHI CHÚ ĐƠN HÀNG
    note,
    //LÝ DO HỦY ĐƠN
    cancelReason,
    //Thông tin đơn vị vận chuyển
    transportUnitInformation,
    //Ảnh giao hàng
    image,

    salesPerson,
  } = all;

  // Tab Tất cả
  const ALL_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    status,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ duyệt đơn
  const WAITING_FOR_APPROVAL_ORDER_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ duyệt tồn
  const WAITING_FOR_APPROVAL_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ đủ tồn
  const WAITING_FOR_ENOUGH_STOCK_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    inStockStatus,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ thanh toán
  const WAITING_FOR_PAYMENT_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ đóng gói
  const WAITING_FOR_PACKAGING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ xuất kho
  const WAITING_FOR_EXPORTING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    shippingType,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ lắp đặt
  const WAITING_FOR_ASSEMBLY_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactAddress,
    shippingType,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ điều phối
  const WAITING_FOR_SHIPPING_COLUMNS_TABLE_NOT_SET = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Chờ giao hàng
  const WAITING_FOR_SHIPPING_COLUMNS_TABLE_WAITING = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Đang giao hàng
  const DELIVERING_COLUMNS_TABLE = [
    createdAt,
    orderType,
    orderCode,
    deliveryDeadline,
    receiverName,
    subTotal,
    exportedWarehouse,
    receiverContactPhone,
    receiverContactAddress,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    paymentStatus,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Hoàn thành
  const COMPLETED_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    transportUnitInformation,
    paymentMethod,
    image,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  // Tab Đã hủy
  const CANCELED_COLUMNS_TABLE = [
    index,
    createdAt,
    orderType,
    orderCode,
    receiverName,
    subTotal,
    exportedWarehouse,
    shippingType,
    paymentMethod,
    paymentStatus,
    cancelReason,
    salesPerson,
    createdByStatus,
    salesChannel,
  ];

  const TABLE_BY_TAB = {
    undefined: ALL_COLUMNS_TABLE,
    [`${STATUS_ORDER.ALL}`]: ALL_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_APPROVAL_ORDER}`]: WAITING_FOR_APPROVAL_ORDER_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_APPROVAL_STOCK}`]: WAITING_FOR_APPROVAL_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_ENOUGH_STOCK}`]: WAITING_FOR_ENOUGH_STOCK_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_PAYMENT}`]: WAITING_FOR_PAYMENT_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_PACKAGING}`]: WAITING_FOR_PACKAGING_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_EXPORTING}`]: WAITING_FOR_EXPORTING_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_ASSEMBLY}`]: WAITING_FOR_ASSEMBLY_COLUMNS_TABLE,
    [`${STATUS_ORDER.WAITING_FOR_SHIPPING_NOT_SET}`]: WAITING_FOR_SHIPPING_COLUMNS_TABLE_NOT_SET,
    [`${STATUS_ORDER.WAITING_FOR_SHIPPING_WAITING}`]: WAITING_FOR_SHIPPING_COLUMNS_TABLE_WAITING,
    [`${STATUS_ORDER.DELIVERING}`]: DELIVERING_COLUMNS_TABLE,
    [`${STATUS_ORDER.COMPLETED}`]: COMPLETED_COLUMNS_TABLE,
    [`${STATUS_ORDER.CANCELED}`]: CANCELED_COLUMNS_TABLE,
  };

  return { TABLE_BY_TAB, renderShippingMethod, renderOrderPriority, renderPaymentMethod };
};

export default useColumnsOrder;
